import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useAuth, canUseAskAI, isLockedDashboardUser } from "./auth";
import catalogue from "./analyticsSearchCatalogue.json";

const EVENT = "camaroo:analytics-favorites-changed";
const errors = new Map();
export const eligibleAnalytics = catalogue.filter(entry => entry.favoriteEligible);
const eligibleIds = new Set(eligibleAnalytics.map(entry => entry.id));
export function favoriteStorageKey(user) {
  const id = user?.id || user?._id?.$oid || user?._id || user?.email;
  if ((typeof id !== "string" && typeof id !== "number") || !String(id)) return null;
  const workspace = user?.workspace_id || "default";
  return `camaroo:analytics-favorites:v1:${encodeURIComponent(String(workspace))}:${encodeURIComponent(String(id))}`;
}
export function parseFavoriteIds(raw) {
  try {
    const data = JSON.parse(raw || "[]");
    if (!Array.isArray(data) || data.some(id => typeof id !== "string")) return [];
    return [...new Set(data)].filter(id => eligibleIds.has(id));
  } catch { return []; }
}
export function groupFavorites(favorites) {
  const groups = new Map();
  for (const entry of favorites) {
    if (!groups.has(entry.tab)) groups.set(entry.tab, { tab: entry.tab, label: entry.tabLabel, items: [] });
    groups.get(entry.tab).items.push(entry);
  }
  return [...groups.values()].sort((a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label));
}
function subscribe(listener) {
  window.addEventListener("storage", listener);
  window.addEventListener(EVENT, listener);
  return () => { window.removeEventListener("storage", listener); window.removeEventListener(EVENT, listener); };
}
function snapshot(key) {
  if (!key || typeof window === "undefined") return "";
  try { return JSON.stringify([window.localStorage.getItem(key) || "[]", errors.get(key) || ""]); }
  catch { return JSON.stringify(["[]", "Browser storage is unavailable. Favorites could not be saved."]); }
}
const serverSnapshot = () => "";
export function useAnalyticsFavorites() {
  const { user } = useAuth();
  const enabled = Boolean(user) && !isLockedDashboardUser(user);
  const key = enabled ? favoriteStorageKey(user) : null;
  const getSnapshot = useCallback(() => snapshot(key), [key]);
  const value = useSyncExternalStore(subscribe, getSnapshot, serverSnapshot);
  const [raw, storageError] = value ? JSON.parse(value) : ["[]", ""];
  const ids = useMemo(() => parseFavoriteIds(raw), [raw]);
  const allowAI = canUseAskAI(user);
  const favorites = useMemo(() => ids.map(id => eligibleAnalytics.find(entry => entry.id === id))
    .filter(entry => entry && (allowAI || entry.tab !== "ai")), [ids, allowAI]);
  const toggle = useCallback((id) => {
    if (!key || !eligibleIds.has(id)) return;
    try {
      const current = parseFavoriteIds(window.localStorage.getItem(key));
      const next = current.includes(id) ? current.filter(item => item !== id) : [...current, id];
      window.localStorage.setItem(key, JSON.stringify(next));
      errors.delete(key);
    } catch { errors.set(key, "Could not save favorites. Check this browser’s storage settings and try again."); }
    window.dispatchEvent(new Event(EVENT));
  }, [key]);
  return { favorites, ids, toggle, enabled: Boolean(key), error: storageError };
}
